import { useCallback, useRef, useState } from 'react'
import { Upload, CheckCircle, Loader2, FileSpreadsheet } from 'lucide-react'
import type { UploadStatus } from '../types'
import { uploadFile } from '../api'

interface UploadPanelProps {
  periodMonth: string
  uploadStatus: UploadStatus
  onUploadComplete: (newStatus: UploadStatus) => void
}

const UPLOAD_CONFIGS = [
  {
    key: 'elocal_leads' as const,
    label: 'eLocal Leads',
    description: 'eLocal call leads CSV',
    color: 'blue',
  },
  {
    key: 'workiz_jobs' as const,
    label: 'Workiz Jobs',
    description: 'Workiz job export CSV',
    color: 'purple',
  },
  {
    key: 'workiz_payments' as const,
    label: 'Workiz Payments',
    description: 'Workiz payment report CSV',
    color: 'green',
  },
]

export function UploadPanel({ periodMonth, uploadStatus, onUploadComplete }: UploadPanelProps) {
  const [uploading, setUploading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const handleUpload = useCallback(
    async (uploadType: string, file: File) => {
      setUploading(uploadType)
      setError(null)
      try {
        const result = await uploadFile(uploadType, periodMonth, file)
        onUploadComplete(result.upload_status)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed')
      } finally {
        setUploading(null)
      }
    },
    [periodMonth, onUploadComplete]
  )

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Upload Data Files</h3>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      <div className="grid grid-cols-3 gap-4">
        {UPLOAD_CONFIGS.map((config) => {
          const status = uploadStatus[config.key]
          const isUploading = uploading === config.key

          return (
            <div
              key={config.key}
              className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                status
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
              }`}
            >
              <input
                ref={(el) => { fileRefs.current[config.key] = el }}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handleUpload(config.key, file)
                  e.target.value = ''
                }}
              />

              {isUploading ? (
                <Loader2 className="w-8 h-8 mx-auto mb-2 text-blue-500 animate-spin" />
              ) : status ? (
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
              ) : (
                <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              )}

              <p className="text-sm font-medium text-gray-700">{config.label}</p>
              <p className="text-xs text-gray-500 mt-1">{config.description}</p>

              {status && (
                <p className="text-xs text-green-600 mt-2">
                  {status.rows} rows &middot; {new Date(status.uploaded_at).toLocaleDateString()}
                </p>
              )}

              <button
                onClick={() => fileRefs.current[config.key]?.click()}
                disabled={isUploading}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" />
                {status ? 'Re-upload' : 'Upload'}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
